// ...existing code...
import React, { useEffect, useState } from 'react';
import {
  Edit,
  SimpleForm,
  TextInput,
  useNotify,
  useRedirect,
  useDataProvider,
  useUpdate,
  useRecordContext,
  useGetOne,
  AutocompleteInput,
} from 'react-admin';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  IconButton,
  FormGroup,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { enNZ } from 'date-fns/locale/en-NZ';
import { AddCircle, RemoveCircle } from '@mui/icons-material';
import ItemSelectInput from '../../components/ItemSelectInput.tsx';
import { useParams } from 'react-router-dom';

import { format } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';

// 新西兰时区
const NZ_TIMEZONE = 'Pacific/Auckland';

// 安全的日期解析函数（按新西兰时区）
const parseDate = (dateInput?: string | Date | null): Date | null => {
  if (!dateInput) return null;

  // 如果已经是 Date，直接返回
  if (dateInput instanceof Date) return dateInput;

  try {
    const dateString = String(dateInput);
    let date: Date;

    // 处理各种日期格式
    if (dateString.includes('T')) {
      date = new Date(dateString);
    } else {
      // 对于 YYYY-MM-DD 格式，按新西兰时区创建日期
      date = new Date(dateString + 'T12:00:00'); // 使用中午时间避免时区边界问题
    }

    // 转换为新西兰时区的日期
    return toZonedTime(date, NZ_TIMEZONE);
  } catch (error) {
    console.error('Error parsing date:', dateInput, error);
    return null;
  }
};

// 格式化日期为新西兰时区的 YYYY-MM-DD 格式
const formatDateForSave = (date?: Date | null): string | null => {
  if (!date) return null;

  try {
    // 转换为新西兰时区
    const nzDate = toZonedTime(date, NZ_TIMEZONE);
    // 格式化为 YYYY-MM-DD
    return format(nzDate, 'yyyy-MM-dd');
  } catch (error) {
    console.error('Error formatting date:', date, error);
    return null;
  }
};

const DocumentEdit = () => {
  // const record = useRecordContext();
  const { id } = useParams<{ id?: string }>();
  const { data: record, isLoading: recordLoading, error: recordError } = useGetOne(
    'prodocDocument',
    { id: id ?? '' },
    { enabled: !!id }
  );

  const notify = useNotify();
  const redirect = useRedirect();
  const dataProvider = useDataProvider();
  const [update] = useUpdate();

  const [totalNetAmount, setTotalNetAmount] = useState<number>(0);
  const [totalToPay, setTotalToPay] = useState<number>(0);
  const [totalCarton, setTotalCarton] = useState<number>(0);
  const [totalNetWeight, setTotalNetWeight] = useState<number>(0);
  const [totalGrossWeight, setTotalGrossWeight] = useState<number>(0);
  const [totalCube, setTotalCube] = useState<number>(0);
  const [prodocProducts, setProdocProducts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<any>(null);
  const [selectedDate, setSelectedDate] = useState<any>(null);
  const [selectedETADate, setSelectedETADate] = useState<any>(null);
  const [selectedETDDate, setSelectedETDDate] = useState<any>(null);

  const [selectedTemplates, setSelectedTemplates] = useState<number[]>([]);
  const [customers, setCustomers] = useState<any[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<any>(null);

  type ProductRow = {
    id?: number; // 后端行主键，存在则为更新
    productId: string | number | null;
    batchNumber: string;
    cartonQuantity: number;
    grossWeight: number;
    netWeight: number;
    unitQuantity: number;
    unitPrice: number;
    cartonPrice: number;
    netAmount: number;
    amountToPay: number;
    cube: number;
    manufactureDate?: Date | null;
    expiryDate?: Date | null;
    isExisting?: boolean; // 用于 UI/逻辑判断（可选）
  };
  const [productRows, setProductRows] = useState<ProductRow[]>([]);

  const templates = [
    { id: 1, name: "Packing-List", variables: ["CustomerName", "OrderDate", "ContainerNum"] },
    { id: 2, name: "Commercial-Invoice", variables: ["CustomerName", "InvoiceNumber", "InvoiceDate"] },
    { id: 3, name: "Packing-Declaration", variables: ["CustomerName", "DeliveryDate", "Items"] },
  ];

  useEffect(() => {
    let isMounted = true;

    const loadAll = async () => {
      try {
        // 1. 加载产品列表
        const res = await dataProvider.getList('ProDocProducts', {
          sort: { field: 'productId', order: 'ASC' },
          pagination: { page: 1, perPage: 1000 },
        });


        // 2. 加载客户列表
        const resCustomers = await dataProvider.getList('prodocCustomer', {
          sort: { field: 'customerName', order: 'ASC' },
          pagination: { page: 1, perPage: 1000 },
        });

        if (!isMounted) return;

        setProdocProducts(res.data);
        setCustomers(resCustomers.data);

        // 2. 初始化 record（在产品加载之后）
        if (record) {
          const rdFormDate = record.formDate ?? record.FormDate;
          const rdETADate = record.eta ?? record.ETA;
          const rdETDDate = record.etd ?? record.ETD;
          const rdTotalNetAmount = record.totalNetAmount ?? record.TotalNetAmount;
          const rdTotalToPay = record.totalToPay ?? record.TotalToPay;
          const rdTotalCarton = record.totalCarton ?? record.TotalCarton;
          const rdTotalNetWeight = record.totalNetWeight ?? record.TotalNetWeight;
          const rdTotalGrossWeight = record.totalGrossWeight ?? record.TotalGrossWeight;
          const rdTotalCube = record.totalCube ?? record.TotalCube;
          const rdTemplateIds = Array.isArray(record.templateIds) ? record.templateIds : record.TemplateIds;
          const rdManufactureDate = record.manufactureDate ?? record.ManufactureDate;
          const rdExpiryDate = record.expiryDate ?? record.ExpiryDate;

          setSelectedDate(parseDate(rdFormDate));
          setSelectedETADate(parseDate(rdETADate));
          setSelectedETDDate(parseDate(rdETDDate));

          setTotalNetAmount(rdTotalNetAmount || 0);
          setTotalToPay(rdTotalToPay || 0);
          setTotalCarton(rdTotalCarton || 0);
          setTotalNetWeight(rdTotalNetWeight || 0);
          setTotalGrossWeight(rdTotalGrossWeight || 0);
          setTotalCube(rdTotalCube || 0);
          setSelectedTemplates(Array.isArray(rdTemplateIds) ? rdTemplateIds : []);


          // 初始化选中的客户（根据 record 中的客户信息查找匹配的客户）
          const matchedCustomer = resCustomers.data.find((c: any) =>
            c.id === record.customerId ||
            c.customerName === record.customerName ||
            c.customerName === record.CustomerName
          );
          setSelectedCustomer(matchedCustomer || null);
          const rows = Array.isArray(record.products) && record.products.length > 0
            ? record.products.map((p: any) => ({
              id: p.productId,
              productId: p.productId ?? null,
              cartonQuantity: p.cartonQuantity ?? '',
              grossWeight: p.grossWeight ?? 0,
              netWeight: p.netWeight ?? 0,
              batchNumber: p.batchNumber ?? '',
              unitQuantity: p.unitQuantity ?? 0,
              unitPrice: p.unitPrice ?? 0,
              cartonPrice: p.cartonPrice ?? 0,
              netAmount: p.netAmount ?? 0,
              amountToPay: p.amountToPay ?? 0,
              cube: p.cube ?? 0,
              manufactureDate: parseDate(p.manufactureDate),
              expiryDate: parseDate(p.expiryDate),
            }))
            : [{ productId: null, cartonNumber: '', quantity: 0, grossWeight: 0, netWeight: 0, cube: 0, unitQuantity: 0, unitPrice: 0, cartonPrice: 0, netAmount: 0, amountToPay: 0 }];
          setProductRows(rows);


        }

        setLoading(false);
      } catch (e) {
        if (!isMounted) return;
        setError(e);
        setLoading(false);
      }
    };

    loadAll();

    return () => {
      isMounted = false;
    };
  }, [dataProvider, record]);



  if (loading) return <span>Loading...</span>;
  if (error) return <span>Error loading product options</span>;

  const handleToggleTemplate = (id: number) => {
    setSelectedTemplates(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));
  };

  const handleCartonChange = (index: number, value: number) => {
    const newRows = [...productRows];
    const carton = value;
    const selectedProduct = prodocProducts.find((p) => p.productId === newRows[index].productId);

    newRows[index].cartonQuantity = carton;
    newRows[index].grossWeight = parseFloat((carton * selectedProduct?.grossWeight * selectedProduct?.cartonSize).toFixed(2));
    newRows[index].netWeight = parseFloat((carton * selectedProduct?.netWeight * selectedProduct?.cartonSize).toFixed(2));
    newRows[index].unitQuantity = carton * selectedProduct?.cartonSize;
    newRows[index].unitPrice = selectedProduct?.productPrice || 0;
    newRows[index].cartonPrice = parseFloat((newRows[index].unitPrice * selectedProduct?.cartonSize).toFixed(2));
    newRows[index].netAmount = parseFloat((newRows[index].cartonPrice * carton).toFixed(2));
    newRows[index].amountToPay = newRows[index].netAmount;
    newRows[index].cube = parseFloat((carton * selectedProduct?.cube).toFixed(2));
    setProductRows(newRows);
  };

  const handleProductChange = (index: number, newProductId: string | null) => {
    const newRows = [...productRows];
    const existingId = newRows[index]?.id;
    const wasExisting = !!existingId;

    // 更改产品时保留行的 id （如果有），并标记是否是已有行
    newRows[index] = {
      ...newRows[index],
      productId: newProductId,
      batchNumber: '',
      cartonQuantity: 0,
      grossWeight: 0,
      netWeight: 0,
      unitQuantity: 0,
      unitPrice: 0,
      cartonPrice: 0,
      netAmount: 0,
      amountToPay: 0,
      cube: 0,
      isExisting: wasExisting,
    };

    setProductRows(newRows);
  };

  const addRow = () => {
    setProductRows([
      ...productRows,
      { productId: null, batchNumber: '', cartonQuantity: 0, grossWeight: 0, netWeight: 0, unitQuantity: 0, unitPrice: 0, cartonPrice: 0, netAmount: 0, amountToPay: 0, cube: 0, manufactureDate: null, expiryDate: null },
    ]);
  };

  const removeRow = (index: number) => {
    setProductRows(productRows.filter((_, i) => i !== index));
  };

  const handleCalculatePrices = () => {
    const totalNetAmount = productRows.reduce((sum, row) => {
      return sum + (row.netAmount || 0);
    }, 0);
    const totalCarton = productRows.reduce((sum, row) => {
      return sum + (row.cartonQuantity || 0);
    }, 0);
    const totalNetWeight = productRows.reduce((sum, row) => {
      return sum + (row.netWeight || 0);
    }, 0);
    const totalGrossWeight = productRows.reduce((sum, row) => {
      return sum + (row.grossWeight || 0);
    }, 0);
    const totalCube = productRows.reduce((sum, row) => {
      return sum + (row.cube || 0);
    }, 0);
    setTotalCarton(totalCarton);
    setTotalNetWeight(parseFloat(totalNetWeight.toFixed(2)));
    setTotalGrossWeight(parseFloat(totalGrossWeight.toFixed(2)));
    setTotalCube(parseFloat(totalCube.toFixed(2)));
    setTotalNetAmount(parseFloat(totalNetAmount.toFixed(2)));
    setTotalToPay(parseFloat(totalNetAmount.toFixed(2)));
  };

  const handleSave = async (values: any) => {
    try {
      // 校验容器相关字段（这些是 TextInput，会在 values 中）
      const requiredFields = ['contractNumber', 'containerNumber', 'sealNumber'];
      for (let field of requiredFields) {
        if (!values[field] || values[field].toString().trim() === '') {
          notify(`Field "${field}" is required`, { type: 'warning' });
          return;
        }
      }

      // 校验客户是否选择
      if (!selectedCustomer) {
        notify('Please select a customer', { type: 'warning' });
        return;
      }

      // 校验客户信息完整性
      // if (!selectedCustomer.customerName || !selectedCustomer.email || !selectedCustomer.address || !selectedCustomer.phone) {
      //   notify('Selected customer is missing required information', { type: 'warning' });
      //   return;
      // }

      for (let i = 0; i < productRows.length; i++) {
        const row = productRows[i];
        if (!row.productId) {
          notify(`Row ${i + 1}: Product is required`, { type: 'warning' });
          return;
        }
        if (!row.cartonQuantity || row.cartonQuantity <= 0) {
          notify(`Row ${i + 1}: Carton Number must be greater than 0`, { type: 'warning' });
          return;
        }
      }

      const documentData = {
        ...record,
        ...values,
        FormDate: formatDateForSave(selectedDate),
        ETA: formatDateForSave(selectedETADate),
        ETD: formatDateForSave(selectedETDDate),


        TotalNetAmount: totalNetAmount,
        totalToPay: totalToPay,
        TotalCarton: totalCarton,
        TotalNetWeight: totalNetWeight,
        TotalGrossWeight: totalGrossWeight,
        TotalCube: totalCube,
        products: productRows.map(row => ({
          ...row,
          manufactureDate: formatDateForSave(row.manufactureDate),
          expiryDate: formatDateForSave(row.expiryDate),
        })),
        TemplateIds: selectedTemplates,
        CustomerName: selectedCustomer.customerName,
        Email: selectedCustomer.email,
        // Address: selectedCustomer.address,
        Building: selectedCustomer.building,
        Road: selectedCustomer.road,
        City: selectedCustomer.city,
        Country: selectedCustomer.country,
        Phone: selectedCustomer.phone,
        Fax: selectedCustomer.fax,
        Attn: selectedCustomer.attn,
        updatedAt: new Date().toISOString(),
      };

      // 使用 react-admin update
      if (!record || !record.id) {
        notify('No record to update', { type: 'warning' });
        return;
      }
      // await update('document', { id: record.id, data: documentData });

      // 调用后端生成 API（可选）
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5180/api';
      try {
        const token = localStorage.getItem("token");
        const res = await fetch(`${API_URL}/prodocDocument/generate`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(documentData),
        });
        if (!res.ok) {
          const errText = await res.text();
          throw new Error(`Failed to generate document: ${errText}`);
        }
        const blob = await res.blob();
        const contentType = res.headers.get("Content-Type");
        let fileName = "";
        const disposition = res.headers.get("Content-Disposition");
        if (disposition && disposition.includes("filename=")) {
          fileName = disposition.split("filename=")[1].replace(/['"]/g, "").trim();
        } else {
          fileName = contentType === "application/zip" ? "Documents.zip" : "Document.docx";
        }
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);

        notify("Document generated successfully!", { type: "success" });
      } catch (err) {
        console.error(err);
        notify("generate doc failed", { type: "error" });
      }

      notify('Document updated successfully', { type: 'success' });
      redirect('list', 'prodocDocument');
    } catch (error) {
      console.error(error);
      notify('Error updating document', { type: 'error' });
    }
  };

  const rowStyle = { display: 'flex', gap: '16px', flexWrap: 'wrap', width: '100%', marginBottom: '0px' };
  const inputStyle = { flex: '1 1 48%', mb: 2 };

  return (
    <Edit>
      <SimpleForm onSubmit={handleSave} toolbar={false} record={record}>
        <Box sx={{ width: '100%', p: 3 }}>
          <Typography variant="h5" gutterBottom> Edit Document </Typography>

          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom color="primary">Container Fields </Typography>
              <Box sx={rowStyle}>
                <TextInput sx={inputStyle} source="containerNumber" label="Container Number" fullWidth />
                <TextInput sx={inputStyle} source="sealNumber" label="Seal Number" fullWidth />
              </Box>

              <Box sx={rowStyle}>
                <TextInput sx={inputStyle} source="contractNumber" label="Contract Number" fullWidth />
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enNZ}>
                  <DatePicker
                    label="Document Date"
                    value={selectedDate}
                    onChange={(newValue) => setSelectedDate(newValue)}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        sx: { ...inputStyle, '& .MuiPickersInputBase-root': { height: 50 }, '& input': { padding: '0 12px' } },
                        inputProps: { placeholder: 'DD/MM/YYYY' },
                      },
                    }}
                  />
                </LocalizationProvider>
              </Box>


              <Box sx={rowStyle}>
                <TextInput sx={inputStyle} source="exporterInvoiceNumber" label="ExporterInvoiceNumber" fullWidth />
                <TextInput sx={inputStyle} source="buyerOrderNumber" label="BuyerOrderNumber" fullWidth />
              </Box>

              <Box sx={rowStyle}>
                <TextInput sx={inputStyle} source="vesselName" label="vesselName" fullWidth />

                <TextInput sx={inputStyle} source="flightVoyageNumber" label="FlightVoyageNumber" fullWidth />
                <TextInput sx={inputStyle} source="vesselVoyageNo" label="VesselVoyageNo" fullWidth />

              </Box>

              <Box sx={rowStyle}>
                <TextInput sx={inputStyle} source="portOfLoading" label="PortOfLoading" fullWidth />
                {/* <TextInput sx={inputStyle} source="portOfDischarge" label="PortOfDischarge" fullWidth /> */}
                <AutocompleteInput
                  source="portOfDischarge"
                  label="PortOfDischarge"
                  choices={[
                    { id: 'Shanghai, China', name: 'Shanghai, China' },

                  ]}
                  optionValue="id"
                  optionText="name"
                  sx={{
                    flex: '1 1 48%',
                    '& .MuiAutocomplete-inputRoot': {
                      minHeight: '50px',
                      height: '50px',
                    },
                    '& .MuiOutlinedInput-input': {
                      height: '50px',
                      padding: '0 12px',
                      boxSizing: 'border-box',
                    },
                    '& .MuiAutocomplete-input': {
                      height: '50px',
                    },
                  }}
                  fullWidth
                />
              </Box>
              <Box sx={rowStyle}>
                <TextInput sx={{
                  flex: '1 1 48%',
                  '& .MuiAutocomplete-inputRoot': {
                    minHeight: '50px',
                    height: '50px',
                  },
                  '& .MuiOutlinedInput-input': {
                    height: '50px',
                    padding: '0 12px',
                    boxSizing: 'border-box',
                  },
                  '& .MuiAutocomplete-input': {
                    height: '50px',
                  },
                }}
                  source="finalDestination" label="FinalDestination" fullWidth />

                {/* <TextInput sx={inputStyle} source="incoterm" label="Incoterm" fullWidth /> */}
                <AutocompleteInput
                  source="incoterm"
                  label="Incoterm"
                  choices={[
                    { id: 'CIF', name: 'CIF' },
                    { id: 'EXW', name: 'EXW' },
                    { id: 'CFR', name: 'CFR' },
                  ]}
                  optionValue="id"
                  optionText="name"
                  sx={{
                    flex: '1 1 48%',
                    '& .MuiAutocomplete-inputRoot': {
                      minHeight: '50px',
                      height: '50px',
                    },
                    '& .MuiOutlinedInput-input': {
                      height: '50px',
                      padding: '0 12px',
                      boxSizing: 'border-box',
                    },
                    '& .MuiAutocomplete-input': {
                      height: '50px',
                    },
                  }}
                  fullWidth
                />
                {/* <TextInput sx={inputStyle} source="incotermPlace" label="IncotermPlace" fullWidth /> */}
                <AutocompleteInput
                  source="incotermPlace"
                  label="incotermPlace"
                  choices={[
                    { id: 'Shanghai', name: 'Shanghai' },

                  ]}
                  optionValue="id"
                  optionText="name"
                  sx={{
                    flex: '1 1 48%',
                    '& .MuiAutocomplete-inputRoot': {
                      minHeight: '50px',
                      height: '50px',
                    },
                    '& .MuiOutlinedInput-input': {
                      height: '50px',
                      padding: '0 12px',
                      boxSizing: 'border-box',
                    },
                    '& .MuiAutocomplete-input': {
                      height: '50px',
                    },
                  }}
                  fullWidth
                />

              </Box>
              <Box sx={rowStyle}>
                <TextInput sx={inputStyle} source="printerName" label="PrinterName" fullWidth />

                <TextInput sx={inputStyle} source="numberLink" label="NumberLink" fullWidth />
              </Box>


              <Box sx={rowStyle}>
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enNZ}>
                  <DatePicker
                    label="ETD"
                    value={selectedETDDate}
                    onChange={(newValue) => setSelectedETDDate(newValue)}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        sx: { ...inputStyle, '& .MuiPickersInputBase-root': { height: 50 }, '& input': { padding: '0 12px' } },
                        inputProps: { placeholder: 'DD/MM/YYYY' },
                      },
                    }}
                  />
                </LocalizationProvider>                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enNZ}>
                  <DatePicker
                    label="ETA"
                    value={selectedETADate}
                    onChange={(newValue) => setSelectedETADate(newValue)}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        sx: { ...inputStyle, '& .MuiPickersInputBase-root': { height: 50 }, '& input': { padding: '0 12px' } },
                        inputProps: { placeholder: 'DD/MM/YYYY' },
                      },
                    }}
                  />
                </LocalizationProvider>
              </Box>

            </CardContent>
          </Card>

          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom color="primary">Customer Fields </Typography>
              <Box sx={rowStyle}>
                <AutocompleteInput
                  source="customerId"
                  label="Customer"
                  choices={customers}
                  optionValue="id"
                  optionText="customerName"
                  value={selectedCustomer?.id || ''}
                  onChange={(customerId) => {
                    const customer = customers.find(c => c.id === customerId);
                    setSelectedCustomer(customer || null);
                  }}
                  sx={inputStyle}
                  fullWidth
                />

              </Box>
              <Box sx={rowStyle}>
                <TextField
                  key={`customerName-${selectedCustomer?.id || 'empty'}`}
                  sx={inputStyle}

                  label="Customer Name"
                  value={selectedCustomer?.customerName || ''}
                  InputProps={{ readOnly: true }}
                  fullWidth
                />
                <TextField
                  key={`email-${selectedCustomer?.id || 'empty'}`}
                  sx={inputStyle}

                  label="Email"
                  value={selectedCustomer?.email || ''}
                  InputProps={{ readOnly: true }}
                  fullWidth
                />
              </Box>
              <Box sx={rowStyle}>
                {/* <TextField
                  key={`address-${selectedCustomer?.id || 'empty'}`}
                  sx={inputStyle}

                  label="Address"
                  value={selectedCustomer?.address || ''}
                  InputProps={{ readOnly: true }}
                  fullWidth
                  multiline
                  rows={4}
                /> */}
                <TextField
                  key={`building-${selectedCustomer?.id || 'empty'}`}
                  sx={inputStyle}

                  label="Building"
                  value={selectedCustomer?.building || ''}
                  InputProps={{ readOnly: true }}
                  fullWidth
                />
                <TextField
                  key={`road-${selectedCustomer?.id || 'empty'}`}
                  sx={inputStyle}
                  label="Road"
                  value={selectedCustomer?.road || ''}
                  InputProps={{ readOnly: true }}
                  fullWidth
                />
                <TextField
                  key={`city-${selectedCustomer?.id || 'empty'}`}
                  sx={inputStyle}

                  label="City"
                  value={selectedCustomer?.city || ''}
                  InputProps={{ readOnly: true }}
                  fullWidth
                />
                <TextField
                  key={`country-${selectedCustomer?.id || 'empty'}`}
                  sx={inputStyle}

                  label="Country"
                  value={selectedCustomer?.country || ''}
                  InputProps={{ readOnly: true }}
                  fullWidth
                />
                <TextField
                  key={`phone-${selectedCustomer?.id || 'empty'}`}
                  sx={inputStyle}

                  label="Phone"
                  value={selectedCustomer?.phone || ''}
                  InputProps={{ readOnly: true }}
                  fullWidth
                />
              </Box>
              <Box sx={rowStyle}>
                <TextField
                  key={`fax-${selectedCustomer?.id || 'empty'}`}
                  sx={inputStyle}

                  label="Fax"
                  value={selectedCustomer?.fax || ''}
                  InputProps={{ readOnly: true }}
                  fullWidth
                />
                <TextField
                  key={`attn-${selectedCustomer?.id || 'empty'}`}
                  sx={inputStyle}

                  label="Attn"
                  value={selectedCustomer?.attn || ''}
                  InputProps={{ readOnly: true }}
                  fullWidth
                />
              </Box>

            </CardContent>
          </Card>

          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom color="primary"> Product Information </Typography>
              {productRows.length > 0 && productRows.map((row, index) => (
                <Box key={index} sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 4, padding: '12px', border: "3px solid  #F7F7F7", borderRadius: 5 }}>
                  {/* 第一行 */}
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <ItemSelectInput
                      sx={{ '& .MuiAutocomplete-inputRoot': { minHeight: 50, height: 50 }, width: '25%', position: "relative", top: "-8px" }}
                      source={`products.${index}`}
                      choices={prodocProducts}
                      value={row.productId || ''}
                      onChange={(value) => handleProductChange(index, value.productId)}
                    />
                    <TextField
                      label="Batch Number"
                      value={row.batchNumber ?? ''}
                      onChange={(e) => {
                        const newRows = [...productRows];
                        newRows[index].batchNumber = e.target.value;
                        setProductRows(newRows);
                      }}
                      sx={{ width: '25%' }}
                      size="small"
                    />
                    <TextField
                      label="Carton Quantity"
                      type="number"
                      value={row.cartonQuantity ?? ''}
                      onChange={(e) => handleCartonChange(index, Number(e.target.value))}
                      sx={{ width: '25%' }}
                      size="small"
                    />
                    <TextField
                      label="Unit Quantity"
                      value={row.unitQuantity ?? ''}
                      InputProps={{ readOnly: true }}
                      sx={{ width: '25%' }}
                      size="small"
                    />
                  </Box>

                  {/* 第二行 */}
                  <Box sx={{ display: 'flex', gap: 2, marginBottom: '30px' }}>


                    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enNZ}>
                      <DatePicker
                        label="Manufacture Date"
                        value={row.manufactureDate}
                        onChange={(newValue) => {
                          setProductRows(prev =>
                            prev.map((row, i) => {
                              if (i !== index) return row;

                              let expiryDate = row.expiryDate;

                              if (newValue) {
                                expiryDate = new Date(newValue);
                                expiryDate.setFullYear(expiryDate.getFullYear() + 2);
                                expiryDate.setDate(expiryDate.getDate() - 1);
                              }

                              return {
                                ...row,
                                manufactureDate: newValue,
                                expiryDate: expiryDate,
                              };
                            })
                          );
                        }}

                        slotProps={{
                          textField: {
                            fullWidth: true,
                            sx: { ...inputStyle, '& .MuiPickersInputBase-root': { height: 50 }, '& input': { padding: '0 12px' } },
                            inputProps: { placeholder: 'DD/MM/YYYY' },
                          },
                        }}
                      />
                    </LocalizationProvider>

                    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enNZ}>
                      <DatePicker
                        label="Expiry Date"
                        value={row.expiryDate}
                        onChange={(newValue) => {
                          const newRows = [...productRows];
                          newRows[index].expiryDate = newValue;
                          setProductRows(newRows);
                        }}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            sx: { ...inputStyle, '& .MuiPickersInputBase-root': { height: 50 }, '& input': { padding: '0 12px' } },
                            inputProps: { placeholder: 'DD/MM/YYYY' },
                          },
                        }}
                      />
                    </LocalizationProvider>

                  </Box>

                  {/* 第二行 */}
                  <Box sx={{ display: 'flex', gap: 2, marginBottom: '30px' }}>
                    <TextField
                      label="Gross Wt"
                      value={row.grossWeight ?? ''}
                      InputProps={{ readOnly: true }}
                      sx={{ width: '25%' }}
                      size="small"
                    />
                    <TextField
                      label="Net Wt"
                      value={row.netWeight ?? ''}
                      InputProps={{ readOnly: true }}
                      sx={{ width: '25%' }}
                      size="small"
                    />
                    <TextField
                      label="Cube"
                      value={row.cube ?? ''}
                      InputProps={{ readOnly: true }}
                      sx={{ width: '25%' }}
                      size="small"
                    />
                    <TextField
                      label="Unit Price"
                      value={row.unitPrice ?? ''}
                      InputProps={{ readOnly: true }}
                      sx={{ width: '25%' }}
                      size="small"
                    />
                  </Box>

                  {/* 第三行 */}
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <TextField
                      label="Carton Price"
                      value={row.cartonPrice ?? ''}
                      InputProps={{ readOnly: true }}
                      sx={{ width: '25%' }}
                      size="small"
                    />
                    <TextField
                      label="Net Amount"
                      value={row.netAmount ?? ''}
                      InputProps={{ readOnly: true }}
                      sx={{ width: '25%' }}
                      size="small"
                    />
                    <TextField
                      label="Amount To Pay"
                      value={row.amountToPay ?? ''}
                      InputProps={{ readOnly: true }}
                      sx={{ width: '25%' }}
                      size="small"
                    />
                    {/* 占位 Box 保持宽度一致 */}
                    <Box sx={{ width: '25%', display: 'flex', gap: 1 }}>
                      <IconButton color="primary" onClick={addRow}><AddCircle /></IconButton>
                      {productRows.length > 1 && <IconButton color="error" onClick={() => removeRow(index)}><RemoveCircle /></IconButton>}
                    </Box>
                  </Box>
                </Box>
              ))}


            </CardContent>
          </Card>

          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom color="primary"> Calculations </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'left', mb: 2 }}>
                <Button variant="contained" color="primary" onClick={handleCalculatePrices}> Calculate Prices </Button>
              </Box>


              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
                <TextField
                  label="TotalNetAmount"
                  value={totalNetAmount}
                  InputProps={{ readOnly: true }}
                  sx={{ width: '25%' }}
                  size="small"
                />
                <TextField
                  label="TotalToPay"
                  value={totalToPay}
                  InputProps={{ readOnly: true }}
                  sx={{ width: '25%' }}
                  size="small"
                />
                <TextField
                  label="TotalCarton"
                  value={totalCarton}
                  InputProps={{ readOnly: true }}
                  sx={{ width: '25%' }}
                  size="small"
                />
                <TextField
                  label="TotalNetWeight"
                  value={totalNetWeight}
                  InputProps={{ readOnly: true }}
                  sx={{ width: '25%' }}
                  size="small"
                />

              </Box>

              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <TextField
                  label="TotalGrossWeight"
                  value={totalGrossWeight}
                  InputProps={{ readOnly: true }}

                  sx={{ width: '50%' }}
                  size="small"
                />
                <TextField
                  label="TotalCube"
                  value={totalCube}
                  InputProps={{ readOnly: true }}
                  sx={{ width: '50%' }}
                  size="small"
                />


              </Box>

            </CardContent>
          </Card>

          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom color="primary"> Document Templates </Typography>
              <FormGroup row>
                {templates.map(t => (
                  <FormControlLabel
                    key={t.id}
                    control={<Checkbox checked={selectedTemplates.includes(t.id)} onChange={() => handleToggleTemplate(t.id)} color="primary" />}
                    label={t.name}
                  />
                ))}
              </FormGroup>
            </CardContent>
          </Card>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button variant="outlined" onClick={() => redirect('list', 'prodocDocument')}>Cancel</Button>
            <Button type="submit" variant="contained">Save And Generate</Button>
          </Box>
        </Box>
      </SimpleForm>
    </Edit>
  );
};

export default DocumentEdit;